import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
    getHourReportingPlacementDetail,
    HourReportingPlacementDetail,
    HourReportingPlacementRow,
    HourReportingPlacementPeriod,
    listHourReportingPlacements,
    listWhatsappTemplates,
    sendWhatsappTemplateBatch,
    WhatsappTemplateComponent,
    WhatsappTemplateSummary,
} from "../api/hourReportingPlacementsApi";

type SortField =
    | "declarationStatus"
    | "workerName"
    | "clientName"
    | "title"
    | "startDate"
    | "endDate";

type SortDirection = "asc" | "desc";

type TemplateParameter = {
    text: string;
    parameterName?: string | null;
};

const DECLARATION_STATUSES = [
    "ontbreekt",
    "onvolledig",
    "gereed",
    "wachten",
    "geaccordeerd",
    "bewerking",
    "afgewikkeld",
    "vervallen",
];

const STATUS_META: Record<string, { label: string; color: string; tone: string }> = {
    ontbreekt: { label: "Ontbreekt", color: "#c2410c", tone: "#fff1e6" },
    onvolledig: { label: "Onvolledig", color: "#b45309", tone: "#fff7d6" },
    gereed: { label: "Gereed", color: "#2563eb", tone: "#e8f0ff" },
    wachten: { label: "Wachten", color: "#6d28d9", tone: "#f3ebff" },
    geaccordeerd: { label: "Geaccordeerd", color: "#15803d", tone: "#e8f7ec" },
    bewerking: { label: "Bewerking", color: "#0f766e", tone: "#e8fbfa" },
    afgewikkeld: { label: "Afgewikkeld", color: "#475569", tone: "#eef2f7" },
    vervallen: { label: "Vervallen", color: "#991b1b", tone: "#feeaea" },
};

function getStatusMeta(status: string | null | undefined) {
    const normalized = String(status || "").trim().toLowerCase();
    return STATUS_META[normalized] ?? {
        label: normalized || "Onbekend",
        color: "#475569",
        tone: "#eef2f7",
    };
}

function formatIsoDate(value: string | null | undefined): string {
    if (!value) {
        return "missing";
    }

    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(parsed.getTime())
        ? String(value)
        : parsed.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
}

function formatDateTime(value: string | null | undefined): string {
    if (!value) {
        return "missing";
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString("nl-NL");
}

function toWeekLabel(period: HourReportingPlacementPeriod | null): string {
    if (!period) {
        return "";
    }
    return `Week ${period.periodNumber} ${period.year}`;
}

function getCurrentIsoWeek(date: Date): { year: number; week: number } {
    const value = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = value.getUTCDay() || 7;
    value.setUTCDate(value.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(value.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((value.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return {
        year: value.getUTCFullYear(),
        week,
    };
}

function getWeekOptions(count = 8) {
    const options: Array<{ id: string; label: string; year: number; week: number }> = [];
    const cursor = new Date();
    cursor.setUTCDate(cursor.getUTCDate() - 7);

    for (let index = 0; index < count; index += 1) {
        const current = new Date(cursor);
        current.setUTCDate(cursor.getUTCDate() - (index * 7));
        const { year, week } = getCurrentIsoWeek(current);
        const id = `${year}-${week}`;
        if (!options.some((item) => item.id === id)) {
            options.push({
                id,
                label: `Week ${week} ${year}`,
                year,
                week,
            });
        }
    }

    return options;
}

function buildProjectLabel(row: HourReportingPlacementRow): string {
    return [row.clientName, row.title || row.functionName].filter(Boolean).join(" - ") || row.organizationName || "Project";
}

function parseNamedParameters(component: WhatsappTemplateComponent | undefined): string[] {
    const named = component?.example?.body_text_named_params;
    if (Array.isArray(named) && named.length) {
        return named
            .map((item) => String(item?.param_name || "").trim())
            .filter(Boolean);
    }

    const bodyText = String(component?.text || "");
    const matches = Array.from(bodyText.matchAll(/\{\{([^}]+)\}\}/g));
    return matches
        .map((match) => String(match[1] || "").trim())
        .filter((item) => Number.isNaN(Number(item)));
}

function getPositionalParameterCount(component: WhatsappTemplateComponent | undefined): number {
    const example = component?.example?.body_text?.[0];
    if (Array.isArray(example) && example.length) {
        return example.length;
    }
    const text = String(component?.text || "");
    const matches = Array.from(text.matchAll(/\{\{(\d+)\}\}/g));
    return matches.length;
}

function buildTemplateParameters(
    template: WhatsappTemplateSummary | null,
    row: HourReportingPlacementRow | null,
    period: HourReportingPlacementPeriod | null,
): TemplateParameter[] {
    if (!template || !row || !period) {
        return [];
    }

    const bodyComponent = template.components?.find((component) => component.type === "BODY");
    const weekLabel = `${period.periodNumber}-${period.year}`;
    const valueMap: Record<string, string> = {
        name: row.workerName,
        project: buildProjectLabel(row),
        week: weekLabel,
    };

    if (template.parameter_format === "NAMED") {
        const names = parseNamedParameters(bodyComponent);
        return names.map((name, index) => ({
            parameterName: name,
            text: valueMap[name.toLowerCase()] ?? [row.workerName, buildProjectLabel(row), weekLabel][index] ?? "",
        }));
    }

    const positionalCount = Math.max(getPositionalParameterCount(bodyComponent), 3);
    const positionalValues = [row.workerName, buildProjectLabel(row), weekLabel];
    return Array.from({ length: positionalCount }, (_, index) => ({
        text: positionalValues[index] ?? "",
    })).filter((parameter) => parameter.text);
}

function getTemplateBody(template: WhatsappTemplateSummary | null): string {
    return template?.components?.find((component) => component.type === "BODY")?.text || "No body content";
}

function renderStatusFilterChip(
    status: string,
    includeStatuses: string[],
    excludeStatuses: string[],
    onToggle: (status: string) => void,
) {
    const meta = getStatusMeta(status);
    const included = includeStatuses.includes(status);
    const excluded = excludeStatuses.includes(status);
    const variant = included ? "filled" : "outlined";
    const label = excluded ? `${meta.label} excluded` : included ? `${meta.label} included` : meta.label;

    return (
        <Chip
            key={status}
            label={label}
            onClick={() => onToggle(status)}
            variant={variant}
            sx={{
                borderColor: meta.color,
                color: excluded ? "#991b1b" : meta.color,
                bgcolor: included ? meta.tone : excluded ? "#feeaea" : "transparent",
                textDecoration: excluded ? "line-through" : "none",
            }}
        />
    );
}

const HourReportingPlacementsPage: React.FC = () => {
    const weekOptions = useMemo(() => getWeekOptions(10), []);
    const defaultWeek = weekOptions[0] ?? { id: "", label: "", year: undefined as unknown as number, week: undefined as unknown as number };

    const [selectedWeekId, setSelectedWeekId] = useState(defaultWeek.id);
    const [placements, setPlacements] = useState<HourReportingPlacementRow[]>([]);
    const [period, setPeriod] = useState<HourReportingPlacementPeriod | null>(null);
    const [templates, setTemplates] = useState<WhatsappTemplateSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [templateLoading, setTemplateLoading] = useState(true);
    const [templatesConfigured, setTemplatesConfigured] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [templateError, setTemplateError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [includeStatuses, setIncludeStatuses] = useState<string[]>(["ontbreekt"]);
    const [excludeStatuses, setExcludeStatuses] = useState<string[]>([]);
    const [linkedOnly, setLinkedOnly] = useState<"all" | "linked" | "unlinked">("all");
    const [sortField, setSortField] = useState<SortField>("declarationStatus");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [detailPlacementId, setDetailPlacementId] = useState<string | null>(null);
    const [detail, setDetail] = useState<HourReportingPlacementDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedTemplateName, setSelectedTemplateName] = useState("");
    const [sendState, setSendState] = useState<"idle" | "sending">("idle");
    const [sendFeedback, setSendFeedback] = useState<string | null>(null);
    const [refreshNonce, setRefreshNonce] = useState(0);

    const selectedWeek = useMemo(
        () => weekOptions.find((option) => option.id === selectedWeekId) ?? defaultWeek,
        [defaultWeek, selectedWeekId, weekOptions],
    );

    useEffect(() => {
        let cancelled = false;

        async function loadPlacements() {
            setLoading(true);
            setError(null);
            try {
                const response = await listHourReportingPlacements({
                    year: selectedWeek.year,
                    periodType: "week",
                    periodNumber: selectedWeek.week,
                });

                if (!cancelled) {
                    setPlacements(response.items);
                    setPeriod(response.period);
                    setSelectedIds([]);
                    setDetailPlacementId(null);
                    setDetail(null);
                }
            } catch (loadError: any) {
                if (!cancelled) {
                    setError(loadError?.response?.data?.message || loadError?.message || "Failed to load placements");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        void loadPlacements();
        return () => {
            cancelled = true;
        };
    }, [refreshNonce, selectedWeek.year, selectedWeek.week]);

    useEffect(() => {
        let cancelled = false;

        async function loadTemplates() {
            setTemplateLoading(true);
            setTemplateError(null);
            try {
                const response = await listWhatsappTemplates();
                if (!cancelled) {
                    setTemplates(response.items);
                    setTemplatesConfigured(response.configured);
                    setTemplateError(response.configured ? null : response.message || "WhatsApp template management is not configured");
                    setSelectedTemplateName((current) => current || response.items.find((template) => template.status === "APPROVED")?.name || "");
                }
            } catch (loadError: any) {
                if (!cancelled) {
                    setTemplatesConfigured(false);
                    setTemplateError(loadError?.response?.data?.message || loadError?.message || "Failed to load WhatsApp templates");
                }
            } finally {
                if (!cancelled) {
                    setTemplateLoading(false);
                }
            }
        }

        void loadTemplates();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!detailPlacementId || !period) {
            setDetail(null);
            return;
        }

        let cancelled = false;

        async function loadDetail() {
            setDetailLoading(true);
            try {
                const response = await getHourReportingPlacementDetail(detailPlacementId, {
                    year: period.year,
                    periodType: "week",
                    periodNumber: period.periodNumber,
                });
                if (!cancelled) {
                    setDetail(response);
                }
            } catch (loadError: any) {
                if (!cancelled) {
                    setDetail(null);
                    setSendFeedback(loadError?.response?.data?.message || loadError?.message || "Failed to load placement detail");
                }
            } finally {
                if (!cancelled) {
                    setDetailLoading(false);
                }
            }
        }

        void loadDetail();
        return () => {
            cancelled = true;
        };
    }, [detailPlacementId, period]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();

        const filtered = placements.filter((row) => {
            if (includeStatuses.length && !includeStatuses.includes(row.declarationStatus)) {
                return false;
            }
            if (excludeStatuses.includes(row.declarationStatus)) {
                return false;
            }
            if (linkedOnly === "linked" && !row.whatsappNumber) {
                return false;
            }
            if (linkedOnly === "unlinked" && row.whatsappNumber) {
                return false;
            }
            if (!query) {
                return true;
            }

            const haystack = [
                row.workerName,
                row.clientName,
                row.title,
                row.functionName,
                row.workerPhoneNumber,
                row.whatsappNumber,
                row.placementNumber,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(query);
        });

        return filtered.sort((left, right) => {
            const direction = sortDirection === "asc" ? 1 : -1;

            const valueFor = (row: HourReportingPlacementRow): string | number => {
                switch (sortField) {
                    case "declarationStatus":
                        return row.declarationStatusSortOrder;
                    case "workerName":
                        return row.workerName || "";
                    case "clientName":
                        return row.clientName || "";
                    case "title":
                        return row.title || row.functionName || "";
                    case "startDate":
                        return row.startDate || "";
                    case "endDate":
                        return row.endDate || "";
                    default:
                        return row.declarationStatusSortOrder;
                }
            };

            const leftValue = valueFor(left);
            const rightValue = valueFor(right);
            if (leftValue < rightValue) {
                return -1 * direction;
            }
            if (leftValue > rightValue) {
                return 1 * direction;
            }
            return String(left.workerName).localeCompare(String(right.workerName));
        });
    }, [excludeStatuses, includeStatuses, linkedOnly, placements, search, sortDirection, sortField]);

    const selectedRows = useMemo(
        () => filteredRows.filter((row) => selectedIds.includes(row.placementId)),
        [filteredRows, selectedIds],
    );

    const selectedTemplate = useMemo(
        () => templates.find((template) => template.name === selectedTemplateName) ?? null,
        [selectedTemplateName, templates],
    );

    const templatePreviewParameters = useMemo(
        () => buildTemplateParameters(selectedTemplate, selectedRows[0] ?? filteredRows[0] ?? null, period),
        [filteredRows, period, selectedRows, selectedTemplate],
    );

    const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedIds.includes(row.placementId));
    const someVisibleSelected = filteredRows.some((row) => selectedIds.includes(row.placementId));

    function toggleStatus(status: string) {
        setSendFeedback(null);
        const currentlyIncluded = includeStatuses.includes(status);
        const currentlyExcluded = excludeStatuses.includes(status);

        if (!currentlyIncluded && !currentlyExcluded) {
            setIncludeStatuses((current) => [...current, status]);
            return;
        }

        if (currentlyIncluded) {
            setIncludeStatuses((current) => current.filter((item) => item !== status));
            setExcludeStatuses((current) => [...current.filter((item) => item !== status), status]);
            return;
        }

        setExcludeStatuses((current) => current.filter((item) => item !== status));
    }

    function handleSort(field: SortField) {
        if (sortField === field) {
            setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
            return;
        }
        setSortField(field);
        setSortDirection("asc");
    }

    function toggleSelection(placementId: string) {
        setSelectedIds((current) =>
            current.includes(placementId)
                ? current.filter((item) => item !== placementId)
                : [...current, placementId],
        );
    }

    function toggleSelectAllVisible() {
        if (allVisibleSelected) {
            setSelectedIds((current) => current.filter((id) => !filteredRows.some((row) => row.placementId === id)));
            return;
        }
        setSelectedIds(Array.from(new Set([...selectedIds, ...filteredRows.map((row) => row.placementId)])));
    }

    async function handleSendTemplate() {
        if (!selectedTemplate || !period || !selectedRows.length) {
            return;
        }

        setSendState("sending");
        setSendFeedback(null);

        try {
            const results = await sendWhatsappTemplateBatch(
                selectedRows.map((row) => ({
                    to: row.whatsappNumber || row.workerPhoneNumber || "",
                    templateName: selectedTemplate.name,
                    languageCode: selectedTemplate.language,
                    parameters: buildTemplateParameters(selectedTemplate, row, period).map((parameter) => ({
                        text: parameter.text,
                        parameterName: parameter.parameterName ?? null,
                    })),
                })),
            );

            const successCount = results.filter((item) => item.ok).length;
            const failureCount = results.length - successCount;
            setSendFeedback(
                failureCount
                    ? `${successCount} message(s) accepted, ${failureCount} failed.`
                    : `${successCount} message(s) accepted by WhatsApp.`,
            );
        } catch (sendError: any) {
            setSendFeedback(sendError?.response?.data?.message || sendError?.message || "Failed to send WhatsApp template");
        } finally {
            setSendState("idle");
        }
    }

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 3, display: "grid", gap: 2.5 }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid #d9e4f2",
                    background: "linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%)",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, display: "grid", gap: 2 }}>
                    <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography variant="overline" sx={{ letterSpacing: 1.1, color: "#355070" }}>
                                Hour Reporting
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                Placements
                            </Typography>
                            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 900 }}>
                                Full-width weekly placement inbox based on active placements and Easyflex declaration status.
                                Default focus stays on last week and missing hour reports.
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Chip
                                icon={<AssignmentOutlinedIcon />}
                                label={period ? `${placements.length} placements in ${toWeekLabel(period)}` : "Loading placements"}
                                sx={{ bgcolor: "#ffffff", border: "1px solid #cfe0f2" }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<RefreshOutlinedIcon />}
                                onClick={() => setRefreshNonce((current) => current + 1)}
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>

                    <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="hour-reporting-week-label">Week</InputLabel>
                            <Select
                                labelId="hour-reporting-week-label"
                                label="Week"
                                value={selectedWeekId}
                                onChange={(event: SelectChangeEvent) => setSelectedWeekId(event.target.value)}
                            >
                                {weekOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="hour-reporting-link-filter-label">Phone link</InputLabel>
                            <Select
                                labelId="hour-reporting-link-filter-label"
                                label="Phone link"
                                value={linkedOnly}
                                onChange={(event: SelectChangeEvent) => setLinkedOnly(event.target.value as "all" | "linked" | "unlinked")}
                            >
                                <MenuItem value="all">All placements</MenuItem>
                                <MenuItem value="linked">Linked to WhatsApp</MenuItem>
                                <MenuItem value="unlinked">No WhatsApp number</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Search placement or worker"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            sx={{ minWidth: 260, flex: 1 }}
                        />
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                            Status include / exclude
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {DECLARATION_STATUSES.map((status) =>
                                renderStatusFilterChip(status, includeStatuses, excludeStatuses, toggleStatus),
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #dde7f3", overflow: "hidden" }}>
                <Toolbar
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        borderBottom: "1px solid #e2e8f0",
                        bgcolor: "#fcfdff",
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox
                            checked={allVisibleSelected}
                            indeterminate={someVisibleSelected && !allVisibleSelected}
                            onChange={toggleSelectAllVisible}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {filteredRows.length} visible placement(s)
                        </Typography>
                        <Button size="small" variant="outlined" onClick={toggleSelectAllVisible}>
                            {allVisibleSelected ? "Clear visible" : "Select all visible"}
                        </Button>
                    </Stack>

                    <Stack direction={{ xs: "column", lg: "row" }} spacing={1} alignItems={{ xs: "stretch", lg: "center" }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id="hour-reporting-template-label">WhatsApp template</InputLabel>
                            <Select
                                labelId="hour-reporting-template-label"
                                label="WhatsApp template"
                                value={selectedTemplateName}
                                onChange={(event: SelectChangeEvent) => setSelectedTemplateName(event.target.value)}
                                disabled={templateLoading || !templates.length}
                            >
                                {!templates.length ? (
                                    <MenuItem value="" disabled>
                                        No templates available
                                    </MenuItem>
                                ) : null}
                                {templates.map((template) => (
                                    <MenuItem key={`${template.id}-${template.language}`} value={template.name}>
                                        {template.name} · {template.category} · {template.language} · {template.status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            startIcon={sendState === "sending" ? <CircularProgress size={16} color="inherit" /> : <ChatBubbleOutlineOutlinedIcon />}
                            disabled={sendState === "sending" || !selectedRows.length || !selectedTemplate || !templatesConfigured}
                            onClick={() => void handleSendTemplate()}
                        >
                            Send to {selectedRows.length || 0} selected
                        </Button>
                    </Stack>
                </Toolbar>

                <Box sx={{ px: { xs: 2, md: 3 }, py: 2, display: "grid", gap: 1.5, bgcolor: "#fafcff" }}>
                    {!templatesConfigured && templateError ? (
                        <Alert severity="info">
                            {templateError}. Set `WHATSAPP_TOKEN` and `WHATSAPP_BUSINESS_ACCOUNT_ID` in `optima-backend` to load live templates here.
                        </Alert>
                    ) : null}
                    {templateError && templatesConfigured ? (
                        <Alert severity={templatesConfigured ? "warning" : "info"}>{templateError}</Alert>
                    ) : null}
                    {sendFeedback ? (
                        <Alert severity={sendFeedback.includes("failed") ? "warning" : "success"}>{sendFeedback}</Alert>
                    ) : null}
                    {selectedTemplate ? (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: "#fffef8" }}>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                                <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {selectedTemplate.name}
                                        </Typography>
                                        <Chip label={selectedTemplate.category} size="small" />
                                        <Chip label={selectedTemplate.status} size="small" variant="outlined" />
                                        <Chip label={selectedTemplate.language} size="small" variant="outlined" />
                                    </Stack>
                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                                        {getTemplateBody(selectedTemplate)}
                                    </Typography>
                                </Box>
                                <Box sx={{ minWidth: { xs: "100%", md: 320 } }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Prefilled parameters
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {templatePreviewParameters.map((parameter, index) => (
                                            <Chip
                                                key={`${parameter.parameterName || index}-${parameter.text}`}
                                                label={`${parameter.parameterName || `param ${index + 1}`}: ${parameter.text}`}
                                                sx={{ justifyContent: "flex-start", height: "auto", "& .MuiChip-label": { py: 0.5 } }}
                                            />
                                        ))}
                                        {!templatePreviewParameters.length ? (
                                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                No parameters detected for this template.
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Paper>
                    ) : null}
                </Box>

                {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}

                <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
                    <Table stickyHeader size="small" aria-label="Hour reporting placements table">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" sx={{ bgcolor: "#f8fbff" }}>
                                    <Checkbox
                                        checked={allVisibleSelected}
                                        indeterminate={someVisibleSelected && !allVisibleSelected}
                                        onChange={toggleSelectAllVisible}
                                    />
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 140 }}>
                                    <TableSortLabel
                                        active={sortField === "declarationStatus"}
                                        direction={sortField === "declarationStatus" ? sortDirection : "asc"}
                                        onClick={() => handleSort("declarationStatus")}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 200 }}>
                                    <TableSortLabel
                                        active={sortField === "workerName"}
                                        direction={sortField === "workerName" ? sortDirection : "asc"}
                                        onClick={() => handleSort("workerName")}
                                    >
                                        Worker
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 240 }}>
                                    <TableSortLabel
                                        active={sortField === "clientName"}
                                        direction={sortField === "clientName" ? sortDirection : "asc"}
                                        onClick={() => handleSort("clientName")}
                                    >
                                        Client / project
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 220 }}>
                                    <TableSortLabel
                                        active={sortField === "title"}
                                        direction={sortField === "title" ? sortDirection : "asc"}
                                        onClick={() => handleSort("title")}
                                    >
                                        Placement
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 180 }}>Week scope</TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 150 }}>WhatsApp</TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 130 }}>
                                    <TableSortLabel
                                        active={sortField === "startDate"}
                                        direction={sortField === "startDate" ? sortDirection : "asc"}
                                        onClick={() => handleSort("startDate")}
                                    >
                                        Start
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f8fbff", minWidth: 130 }}>
                                    <TableSortLabel
                                        active={sortField === "endDate"}
                                        direction={sortField === "endDate" ? sortDirection : "asc"}
                                        onClick={() => handleSort("endDate")}
                                    >
                                        End
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
                                            <CircularProgress size={18} />
                                            <Typography variant="body2">Loading placements…</Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && !filteredRows.length ? (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                                            No placements match the current week and filters.
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && filteredRows.map((row) => {
                                const statusMeta = getStatusMeta(row.declarationStatus);
                                const checked = selectedIds.includes(row.placementId);
                                return (
                                    <TableRow
                                        key={row.placementId}
                                        hover
                                        selected={checked}
                                        onClick={() => setDetailPlacementId(row.placementId)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => toggleSelection(row.placementId)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0.75}>
                                                <Chip
                                                    label={statusMeta.label}
                                                    size="small"
                                                    sx={{
                                                        width: "fit-content",
                                                        bgcolor: statusMeta.tone,
                                                        color: statusMeta.color,
                                                        fontWeight: 700,
                                                    }}
                                                />
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    {row.processStatusWorker ? `FW ${row.processStatusWorker}` : "No worker process status"}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {row.workerName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    {row.workerPhoneNumber || "No phone on file"}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {row.clientName || row.organizationName || "Unknown client"}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    {row.organizationName || "Unknown organization"}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2">{row.title || row.functionName || "Unnamed placement"}</Typography>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    #{row.placementNumber ?? "?"}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2">{toWeekLabel(period)}</Typography>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    {formatIsoDate(row.declarationPeriod.start)} - {formatIsoDate(row.declarationPeriod.end)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {row.whatsappNumber ? (
                                                <Chip
                                                    icon={<CheckCircleOutlineOutlinedIcon />}
                                                    label={row.whatsappNumber}
                                                    size="small"
                                                    sx={{ bgcolor: "#edfdf3", color: "#15803d" }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<WarningAmberOutlinedIcon />}
                                                    label="Missing WhatsApp"
                                                    size="small"
                                                    sx={{ bgcolor: "#fff1e6", color: "#c2410c" }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{formatIsoDate(row.startDate)}</TableCell>
                                        <TableCell>{formatIsoDate(row.endDate)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Drawer
                anchor="right"
                open={Boolean(detailPlacementId)}
                onClose={() => setDetailPlacementId(null)}
                PaperProps={{ sx: { width: { xs: "100%", md: 620 } } }}
            >
                <Box sx={{ p: 3, display: "grid", gap: 2 }}>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography variant="overline" sx={{ color: "text.secondary" }}>
                                Placement detail
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {detail?.item?.workerName || "Loading…"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {detail?.item?.clientName || detail?.item?.organizationName || "Unknown client"} · {detail?.item?.title || detail?.item?.functionName || "Unnamed placement"}
                            </Typography>
                        </Box>
                        {detail ? (
                            <Chip
                                label={getStatusMeta(detail.item.declarationStatus).label}
                                sx={{
                                    bgcolor: getStatusMeta(detail.item.declarationStatus).tone,
                                    color: getStatusMeta(detail.item.declarationStatus).color,
                                    fontWeight: 700,
                                }}
                            />
                        ) : null}
                    </Stack>

                    {detailLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2">Loading placement detail…</Typography>
                        </Stack>
                    ) : null}

                    {detail ? (
                        <>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Overview
                                    </Typography>
                                    <Typography variant="body2">Week: {toWeekLabel(detail.period)}</Typography>
                                    <Typography variant="body2">Placement number: {detail.item.placementNumber ?? "missing"}</Typography>
                                    <Typography variant="body2">WhatsApp: {detail.item.whatsappNumber || detail.item.workerPhoneNumber || "missing"}</Typography>
                                    <Typography variant="body2">Declaration period: {formatIsoDate(detail.item.declarationPeriod.start)} - {formatIsoDate(detail.item.declarationPeriod.end)}</Typography>
                                    <Typography variant="body2">Context error: {detail.item.latestContextError || "none"}</Typography>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Worked time from workflow
                                    </Typography>
                                    {detail.workflowDraftLines.length ? (
                                        detail.workflowDraftLines.map((line, index) => (
                                            <Paper key={`${line.date || "na"}-${index}`} variant="outlined" sx={{ p: 1.5, bgcolor: "#fcfaf5" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {formatIsoDate(line.date)} · {line.startTime || "?"} - {line.endTime || "?"}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                    Break {line.breakMinutes ?? 0}m · Worked {line.workedMinutes ?? "missing"}m · Source {line.source}
                                                </Typography>
                                                {line.remarks ? (
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                        {line.remarks}
                                                    </Typography>
                                                ) : null}
                                            </Paper>
                                        ))
                                    ) : (
                                        <Alert severity="info">No start/end/break lines were stored for this placement in the selected week.</Alert>
                                    )}
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Easyflex declaration rows
                                    </Typography>
                                    {Array.isArray(detail.declarationContext?.currentRows?.hours) && detail.declarationContext.currentRows.hours.length ? (
                                        detail.declarationContext.currentRows.hours.map((line: any, index: number) => (
                                            <Stack
                                                key={`${line.id || line.date || index}`}
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1}
                                                justifyContent="space-between"
                                                sx={{ py: 1, borderBottom: index < detail.declarationContext.currentRows.hours.length - 1 ? "1px solid #edf2f7" : "none" }}
                                            >
                                                <Typography variant="body2">
                                                    {formatIsoDate(line.date)} · Looncomponent {line.looncomponentId ?? "?"}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                                    {line.minutesWorker ?? line.minutes ?? 0} minute(s)
                                                </Typography>
                                            </Stack>
                                        ))
                                    ) : (
                                        <Alert severity="info">No declaration rows were found for the selected week.</Alert>
                                    )}
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Latest workflow
                                    </Typography>
                                    {detail.latestWorkflow ? (
                                        <>
                                            <Typography variant="body2">Status: {detail.latestWorkflow.status} · {detail.latestWorkflow.workflowState}</Typography>
                                            <Typography variant="body2">Updated: {formatDateTime(detail.latestWorkflow.updatedAt)}</Typography>
                                            <Typography variant="body2">Created: {formatDateTime(detail.latestWorkflow.createdAt)}</Typography>
                                            {detail.latestWorkflow.attachment?.fileName ? (
                                                <Typography variant="body2">
                                                    Attachment: {detail.latestWorkflow.attachment.fileName}
                                                </Typography>
                                            ) : null}
                                        </>
                                    ) : (
                                        <Alert severity="info">No related workflow was found for this placement.</Alert>
                                    )}
                                </Stack>
                            </Paper>

                            <Divider />
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                Click outside the drawer to return to the placements table.
                            </Typography>
                        </>
                    ) : null}
                </Box>
            </Drawer>
        </Box>
    );
};

export default HourReportingPlacementsPage;
